import { useState, useMemo } from 'react';
import {
  ThumbsUp,
  MessageCircle,
  Repeat2,
  Send,
  MoreHorizontal,
  Monitor,
  Smartphone,
  PartyPopper,
  Heart,
  FileText,
} from 'lucide-react';
import { cn } from '../lib/utils';
import { getMediaType } from '../lib/media';

const LinkedInPostPreview = ({ content, attachedMedia, clientName }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [viewMode, setViewMode] = useState('desktop');

  const formatContent = (text, expanded, isMobile) => {
    if (!text) return null;
    if (expanded) return { displayText: text, needsTruncation: false };
    const lines = text.split('\n');
    const words = text.split(' ');
    const lineLimit = isMobile ? 2 : 3;
    const wordLimit = isMobile ? 20 : 30;
    let displayText = text;
    let truncated = false;
    if (lines.length > lineLimit) {
      displayText = lines.slice(0, lineLimit).join('\n');
      truncated = true;
    } else if (words.length > wordLimit) {
      displayText = words.slice(0, wordLimit).join(' ');
      truncated = true;
    }
    return { displayText, needsTruncation: truncated };
  };

  const contentFormatted = useMemo(
    () => formatContent(content, isExpanded, viewMode === 'mobile'),
    [content, isExpanded, viewMode]
  );

  const isMobile = viewMode === 'mobile';

  return (
    <div className="space-y-3">
      {/* View toggle */}
      <div className="flex items-center justify-between">
        <p className="text-xs text-muted-foreground">How your post will appear on LinkedIn</p>
        <div className="flex items-center gap-1 bg-muted rounded-lg p-1">
          {[
            { mode: 'desktop', Icon: Monitor },
            { mode: 'mobile', Icon: Smartphone },
          ].map(({ mode, Icon }) => (
            <button
              key={mode}
              onClick={() => { setViewMode(mode); setIsExpanded(false); }}
              className={cn(
                'flex items-center gap-1.5 h-7 px-2.5 rounded-md text-xs transition-all',
                viewMode === mode
                  ? 'bg-background shadow-sm text-foreground'
                  : 'text-muted-foreground hover:text-foreground'
              )}
            >
              <Icon className="w-3.5 h-3.5" />
              <span className="capitalize">{mode}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Post card */}
      <div className={cn('mx-auto transition-all duration-300', isMobile ? 'max-w-[375px]' : 'max-w-full')}>
        <div className="bg-card border border-border rounded-lg overflow-hidden shadow-sm">
          {/* Header */}
          <div className={cn('flex items-start gap-2', isMobile ? 'p-2' : 'p-3')}>
            <div className={cn(
              'rounded-full bg-gradient-to-br from-[#556B2F] to-[#6B8E23] flex items-center justify-center text-white font-bold flex-shrink-0',
              isMobile ? 'w-8 h-8 text-sm' : 'w-10 h-10 text-base'
            )}>
              {clientName?.charAt(0)?.toUpperCase() || 'C'}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-1">
                <span className={cn('font-semibold text-foreground truncate', isMobile ? 'text-xs' : 'text-sm')}>
                  {clientName || 'Client Name'}
                </span>
                <span className={cn('text-muted-foreground flex-shrink-0', isMobile ? 'text-[10px]' : 'text-xs')}>
                  • You
                </span>
              </div>
              <div className="flex items-center gap-1 mt-0.5">
                <span className={cn('text-muted-foreground', isMobile ? 'text-[10px]' : 'text-xs')}>Now •</span>
                <svg
                  className={cn('text-muted-foreground', isMobile ? 'w-2.5 h-2.5' : 'w-3 h-3')}
                  fill="currentColor"
                  viewBox="0 0 16 16"
                >
                  <path d="M8 0a8 8 0 1 0 0 16A8 8 0 0 0 8 0zm0 2a6 6 0 1 1 0 12A6 6 0 0 1 8 2z" />
                </svg>
              </div>
            </div>
            <button className="text-muted-foreground hover:bg-muted rounded p-1 transition-colors flex-shrink-0">
              <MoreHorizontal className="w-4 h-4" />
            </button>
          </div>

          {/* Content */}
          <div className={cn('border-t border-border', isMobile ? 'px-2 py-2' : 'px-3 py-3')}>
            {contentFormatted ? (
              <div className={cn('text-foreground leading-relaxed', isMobile ? 'text-xs' : 'text-sm')}>
                <div className="whitespace-pre-wrap break-words">
                  {contentFormatted.displayText}
                  {contentFormatted.needsTruncation && !isExpanded && (
                    <>
                      <span>…</span>
                      <button
                        onClick={() => setIsExpanded(true)}
                        className="text-primary font-medium ml-1 hover:underline text-xs"
                      >
                        more
                      </button>
                    </>
                  )}
                </div>
                {isExpanded && contentFormatted.needsTruncation && (
                  <button
                    onClick={() => setIsExpanded(false)}
                    className="text-primary font-medium mt-1 hover:underline block text-xs"
                  >
                    Show less
                  </button>
                )}
              </div>
            ) : (
              <div className={cn(
                'text-muted-foreground italic bg-muted rounded border border-dashed border-border',
                isMobile ? 'text-[10px] p-2' : 'text-xs p-3'
              )}>
                Write your post copy to see a preview here…
              </div>
            )}
          </div>

          {/* Media */}
          {attachedMedia && (
            <div className="w-full bg-muted border-t border-border">
              {getMediaType(attachedMedia) === 'video' ? (
                <video
                  src={attachedMedia}
                  controls
                  className="w-full h-auto bg-black"
                  style={{ maxHeight: isMobile ? '280px' : '400px' }}
                />
              ) : getMediaType(attachedMedia) === 'pdf' ? (
                <a
                  href={attachedMedia}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 px-3 py-4 text-sm text-[#0a66c2] hover:underline"
                >
                  <FileText className="w-5 h-5 shrink-0" />
                  {attachedMedia.split('/').pop() || 'Document.pdf'}
                </a>
              ) : (
                <img
                  src={attachedMedia}
                  alt="Post media"
                  className="w-full h-auto object-cover"
                  style={{ maxHeight: isMobile ? '280px' : '400px' }}
                  referrerPolicy="no-referrer"
                  onError={(e) => { e.target.style.display = 'none'; }}
                />
              )}
            </div>
          )}

          {/* Engagement stats */}
          <div className={cn('border-t border-border flex justify-between items-center', isMobile ? 'px-2 py-1.5' : 'px-3 py-2')}>
            <div className="flex items-center gap-1">
              <div className="flex -space-x-1">
                <div className="bg-blue-600 rounded-full w-4 h-4 border border-card flex items-center justify-center">
                  <ThumbsUp className="w-2 h-2 text-white" />
                </div>
                <div className="bg-red-500 rounded-full w-4 h-4 border border-card flex items-center justify-center">
                  <Heart className="w-2 h-2 text-white" />
                </div>
                <div className="bg-green-600 rounded-full w-4 h-4 border border-card flex items-center justify-center">
                  <PartyPopper className="w-2 h-2 text-white" />
                </div>
              </div>
              <span className={cn('text-muted-foreground', isMobile ? 'text-[10px]' : 'text-xs')}>12.5k</span>
            </div>
            <div className={cn('text-muted-foreground flex items-center gap-1', isMobile ? 'text-[10px]' : 'text-xs')}>
              <span>248 comments</span>
              {!isMobile && <><span>•</span><span>65 reposts</span></>}
            </div>
          </div>

          {/* Action buttons */}
          <div className="border-t border-border flex divide-x divide-border">
            {[
              { Icon: ThumbsUp, label: 'Like' },
              { Icon: MessageCircle, label: 'Comment' },
              { Icon: Repeat2, label: 'Repost' },
              { Icon: Send, label: 'Send' },
            ].map(({ Icon, label }) => (
              <button
                key={label}
                className={cn(
                  'flex-1 flex items-center justify-center gap-1 hover:bg-muted text-muted-foreground hover:text-primary transition-colors',
                  isMobile ? 'py-1.5' : 'py-2'
                )}
              >
                <Icon className="w-4 h-4" />
                {!isMobile && <span className="text-xs font-medium hidden sm:inline">{label}</span>}
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default LinkedInPostPreview;
